import React, {useEffect, useState} from 'react';
import {useForm, SubmitHandler} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {useDispatch, useSelector} from 'react-redux';
import {AuthActions, AuthEffects} from '../../store/auth';
import { useLocation } from 'react-router';
import { push } from 'connected-react-router'
import {debounce, parseQuery} from "../../util/helper-methods";
import {RootState} from "../../store";
import {Routes} from "../../routes";
import {getPasswordStrength} from "../../util/helper-methods";
import {ResetPasswordFormData, StrengthObjType} from "../../util/interfaces";
import {defaultMessageObj, defaultStrengthObj} from "../../util/constants";
import PasswordStrength from "../../components/password-strength/password-strength";
import {
  Form, FormCard, FormCardHeader, FormCardTransition, FormCardVariants,
  Line,
  LinkContainer, StyledField, StyledLink, SubmitButton
} from "styles/styled";
import {PasswordInput} from "shared-react";
import MessageCard from "../../components/message-card";
import {motion} from "framer-motion";
import useDocumentTitle from "../../util/hooks/useDocumentTitle";

const schema = yup.object().shape({
  password: yup.string(),
});

const ResetPassword = () => {

  useDocumentTitle('Reset Password');
  const dispatch = useDispatch();
  const { search } = useLocation();
  const [passwordStrength, setPasswordStrength] = useState<StrengthObjType>(defaultStrengthObj);
  const resetKey = useSelector((state: RootState) => state.auth.resetKey);
  const resetPasswordStatus = useSelector((state: RootState) => state.auth.resetPasswordStatus);

  useEffect(() => {
    const newResetKey = parseQuery(search, 'resetKey');
    if(!resetKey){
      if(newResetKey) {
        dispatch(AuthActions.setResetKey(newResetKey));
      } else {
        dispatch(push(Routes.FORGOT_PASSWORD));
      }
    }
    if(resetKey && newResetKey) {
      dispatch(push(Routes.RESET_PASSWORD));
    }
  }, [resetKey, search, dispatch]);


  const { register, handleSubmit, setValue } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit: SubmitHandler<ResetPasswordFormData> = (data: ResetPasswordFormData) => {
    dispatch(AuthEffects.startResetPassword(data));
  };

  const handleReturnToLogin = () => {
    //this should probably be combined in an effect
    dispatch(AuthActions.clearResetPasswordStatus());
    window.location.assign('/authenticate/login');
  };

  let messageObj = {...defaultMessageObj};
  switch(resetPasswordStatus) {
    case "error":
      messageObj = {
        message: 'An error occurred resetting your password. The reset password token may have timed out or been invalidated.',
        type: 'warning'
      }
      break;
    case "complete":
      messageObj = {
        message: 'Password Reset Successful.',
        type: 'success'
      }
      break;
    case "disabled":
      messageObj = {
        message: 'Reset password token has timed out or been invalidated.',
        type: 'warning'
      }
      break;
    default:
      messageObj = {
        message: null,
        type: 'warning'
      }
      break;
  }

  // using this to overwrite the change handler of password (overwritten hook form handler doesn't handle autofill)
  const passwordChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('password', event.target.value);
    determineStrength(event);
  }

  const determineStrength = (event: React.ChangeEvent<HTMLInputElement>) => {
    const temp = getPasswordStrength(event.target.value);
    setPasswordStrength(temp);
  }

  const disableEdit = resetPasswordStatus === 'disabled' || resetPasswordStatus === 'complete';

  return (
    <motion.div
      initial="out"
      animate="in"
      exit="out"
      transition={FormCardTransition}
      variants={FormCardVariants}
    >
      <FormCard>
        <FormCardHeader>Reset Password</FormCardHeader>
        <Line/>
        <MessageCard type={messageObj.type}>
          {messageObj.message}
        </MessageCard>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <StyledField label="Password">
            <PasswordInput aria-label="Password" type="password" disabled={disableEdit} placeholder="Password"{...register('password', )} onChange={debounce(passwordChangeHandler)}/>
            </StyledField>
          {passwordStrength.description && <PasswordStrength color={passwordStrength.color} value={passwordStrength.value} copy={passwordStrength.description as string}/>}
            <SubmitButton type="submit" aria-label="submit" disabled={passwordStrength.value < 75 || disableEdit}>Reset Password</SubmitButton>
          </Form>
      </FormCard>
      <LinkContainer>
        <StyledLink as="a" aria-label="return to login" role='button' onClick={handleReturnToLogin}>Return to login</StyledLink>
      </LinkContainer>
    </motion.div>
  );
};

export default ResetPassword;
